/**
 * Reel Widget — shoppable video rail + Instagram-style fullscreen player.
 *
 * Zero dependencies. Works anywhere you can drop a <script> tag.
 *
 *   <div data-reel-widget data-src="data/reels.json"></div>
 *   <script src="src/reel-widget.js"></script>
 *
 * Or mount it yourself for full control:
 *
 *   ReelWidget.mount(document.querySelector('#reels'), {
 *     items: [...],
 *     onAddToCart: async (product) => { ... },
 *   });
 *
 * See README.md for the item schema and platform wiring.
 */
(function (global) {
  'use strict';

  var REDUCED = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  function el(tag, cls, attrs) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    for (var k in attrs || {}) {
      if (attrs[k] != null) n.setAttribute(k, attrs[k]);
    }
    return n;
  }

  function money(v, opts) {
    if (v == null) return '';
    try {
      return new Intl.NumberFormat(opts.locale, {
        style: 'currency',
        currency: opts.currency,
        maximumFractionDigits: 0,
      }).format(v);
    } catch (e) {
      return opts.currency + ' ' + v;
    }
  }

  function views(n) {
    if (n == null) return null;
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M Views';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K Views';
    return n + ' Views';
  }

  function discount(p) {
    if (!p || !p.compareAt || !p.price || p.compareAt <= p.price) return null;
    return Math.round(((p.compareAt - p.price) / p.compareAt) * 100) + '% Off';
  }

  // Inline SVGs so the widget stays a single self-contained file.
  var ICON = {
    heart: '<svg viewBox="0 0 24 24"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>',
    share: '<svg viewBox="0 0 24 24"><path d="M22 2 11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>',
    close: '<svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    prev: '<svg viewBox="0 0 24 24"><path d="M15 18 9 12l6-6"/></svg>',
    next: '<svg viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"/></svg>',
    play: '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>',
    soundOn: '<svg viewBox="0 0 24 24"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M19.1 4.9a10 10 0 0 1 0 14.2M15.5 8.5a5 5 0 0 1 0 7"/></svg>',
    soundOff: '<svg viewBox="0 0 24 24"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>',
  };

  function iconBtn(cls, name, label) {
    var b = el('button', 'rw__icon ' + (cls || ''), { type: 'button', 'aria-label': label });
    b.innerHTML = ICON[name];
    return b;
  }

  /** Chrome/Safari reject play() when not allowed; never let it throw. */
  function safePlay(video) {
    if (!video) return;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  /* ------------------------------------------------------- default add-to-cart */

  /** Shopify AJAX cart. Override via options.onAddToCart for other platforms. */
  async function shopifyAddToCart(product) {
    if (!product.variantId) {
      throw new Error('Item is missing product.variantId — cannot add to cart.');
    }
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: product.variantId, quantity: 1 }] }),
    });
    if (!res.ok) {
      var msg = await res.text().catch(function () { return ''; });
      throw new Error(msg || 'Add to cart failed (' + res.status + ')');
    }
    return res.json();
  }

  /* =================================================================== Player */

  function Player(items, opts) {
    var self = this;
    this.items = items;
    this.opts = opts;
    this.index = 0;
    this.muted = true;
    this.lastFocus = null;
    this.scrollY = 0;

    var root = el('div', 'rw-player', {
      hidden: '',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': 'Shoppable videos',
    });

    // --- top bar: progress + close
    var top = el('div', 'rw-player__top');
    var progress = el('div', 'rw-player__progress');
    var tick = el('div', 'rw-player__tick');
    this.bar = el('i');
    tick.appendChild(this.bar);
    progress.appendChild(tick);
    var close = iconBtn('', 'close', 'Close videos');
    close.addEventListener('click', function () { self.close(); });
    top.appendChild(progress);
    top.appendChild(close);

    // --- scroller with one reel per item
    var scroller = el('div', 'rw-player__scroller');
    this.reels = items.map(function (item, i) { return self._reel(item, i); });
    this.reels.forEach(function (r) { scroller.appendChild(r.node); });

    this.toast = el('div', 'rw-player__toast', { role: 'status', 'aria-live': 'polite' });

    root.appendChild(top);
    root.appendChild(scroller);
    root.appendChild(this.toast);
    document.body.appendChild(root);

    this.node = root;
    this.scroller = scroller;
    this.closeBtn = close;

    // Track which reel is on screen. scroll-snap does the moving; we just react.
    this.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          var i = self.reels.findIndex(function (r) { return r.node === e.target; });
          if (i > -1 && i !== self.index) self._activate(i);
        }
      });
    }, { root: scroller, threshold: [0.6] });
    this.reels.forEach(function (r) { self.io.observe(r.node); });

    root.addEventListener('keydown', function (e) { self._keydown(e); });
  }

  Player.prototype._reel = function (item, i) {
    var self = this;
    var opts = this.opts;
    var p = item.product || {};

    var node = el('article', 'rw-reel', { 'aria-roledescription': 'reel', 'aria-label': p.title || 'Video ' + (i + 1) });

    if (item.poster) node.appendChild(el('img', 'rw-reel__poster', { src: item.poster, alt: '' }));

    var video = el('video', '', {
      playsinline: '',
      'webkit-playsinline': '',
      loop: '',
      preload: 'none',
      poster: item.poster || null,
    });
    video.muted = true;          // property, not just attribute — iOS needs this
    video.loop = true;
    video.playsInline = true;
    node.appendChild(video);

    // Tap anywhere: toggle play/pause
    var tap = el('button', 'rw-reel__tap', { type: 'button', 'aria-label': 'Play or pause' });
    tap.addEventListener('click', function () { self.togglePlay(); });
    node.appendChild(tap);

    var pauseIcon = el('div', 'rw-reel__pauseIcon');
    pauseIcon.innerHTML = ICON.play;
    node.appendChild(pauseIcon);

    // --- side actions: like / share / mute
    var actions = el('div', 'rw-reel__actions');

    var likeWrap = el('div', 'rw-reel__actionWrap');
    var like = iconBtn('', 'heart', 'Like this video');
    var likeCount = el('span', 'rw-reel__actionLabel');
    likeCount.textContent = item.likes != null ? item.likes : '';
    like.addEventListener('click', function () {
      var on = like.classList.toggle('is-liked');
      like.setAttribute('aria-pressed', String(on));
      if (item.likes != null) likeCount.textContent = item.likes + (on ? 1 : 0);
      if (opts.onLike) opts.onLike(item, on);
    });
    likeWrap.appendChild(like);
    likeWrap.appendChild(likeCount);

    var mute = iconBtn('', 'soundOff', 'Unmute');
    mute.addEventListener('click', function () { self.toggleMute(); });

    actions.appendChild(likeWrap);
    actions.appendChild(mute);
    node.appendChild(actions);

    // --- bottom panel: caption + buy
    var panel = el('div', 'rw-reel__panel');
    if (item.caption) {
      var cap = el('p', 'rw-reel__caption');
      cap.textContent = item.caption;
      panel.appendChild(cap);
    }

    if (p.title) {
      var buy = el('div', 'rw-reel__buy');
      buy.appendChild(el('img', 'rw__thumb', { src: p.image || item.poster, alt: '', loading: 'lazy' }));

      var info = el('div');
      var t = el('p', 'rw__title');
      t.textContent = p.title;
      info.appendChild(t);

      var price = el('div', 'rw__price');
      var now = el('span', 'rw__now');
      now.textContent = money(p.price, opts);
      price.appendChild(now);
      if (p.compareAt && p.compareAt > p.price) {
        var was = el('span', 'rw__was');
        was.textContent = money(p.compareAt, opts);
        price.appendChild(was);
      }
      var off = discount(p);
      if (off) {
        var o = el('span', 'rw__off');
        o.textContent = off;
        price.appendChild(o);
      }
      info.appendChild(price);
      buy.appendChild(info);

      var cta = el('button', 'rw-reel__cta', { type: 'button' });
      cta.textContent = p.cta || 'Add to cart';
      cta.addEventListener('click', function () { self._add(p, cta); });
      buy.appendChild(cta);

      panel.appendChild(buy);
    }
    node.appendChild(panel);

    return { node: node, video: video, mute: mute, item: item };
  };

  Player.prototype._share = async function (item) {
    var url = (item.product && item.product.url) || location.href;
    var title = (item.product && item.product.title) || document.title;
    try {
      if (navigator.share) {
        await navigator.share({ title: title, url: url });
      } else {
        await navigator.clipboard.writeText(url);
        this.showToast('Link copied');
      }
    } catch (e) {
      /* user dismissed the share sheet — not an error */
    }
  };

  Player.prototype._add = async function (product, btn) {
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      await this.opts.onAddToCart(product);
      btn.textContent = 'Added ✓';
      this.showToast('Added to cart');
      if (this.opts.closeOnAdd) this.close();
    } catch (e) {
      this.showToast(e.message || 'Could not add to cart');
      btn.textContent = label;
    } finally {
      setTimeout(function () {
        btn.disabled = false;
        btn.textContent = label;
      }, 1600);
    }
  };

  Player.prototype.showToast = function (msg) {
    var t = this.toast;
    t.textContent = msg;
    t.classList.add('is-shown');
    clearTimeout(this._toastT);
    this._toastT = setTimeout(function () { t.classList.remove('is-shown'); }, 2200);
  };

  /** Only keep src on current ±1 so we never download the whole rail. */
  Player.prototype._hydrate = function () {
    var self = this;
    this.reels.forEach(function (r, i) {
      var near = Math.abs(i - self.index) <= 1;
      if (near && !r.video.src) {
        r.video.src = r.item.video;
      } else if (!near && r.video.src) {
        r.video.pause();
        r.video.removeAttribute('src');
        r.video.load();
      }
    });
  };

  Player.prototype._activate = function (i) {
    var self = this;
    this.index = i;
    this._hydrate();

    this.reels.forEach(function (r, j) {
      if (j === i) return;
      r.video.pause();
      if (r.video.currentTime) r.video.currentTime = 0;
      r.node.classList.remove('is-paused');
    });

    var cur = this.reels[i];
    cur.video.muted = this.muted;
    if (!REDUCED) safePlay(cur.video);

    // Progress bar follows the active video only.
    if (this._onTime) this._timeEl && this._timeEl.removeEventListener('timeupdate', this._onTime);
    this._timeEl = cur.video;
    this._onTime = function () {
      var d = cur.video.duration;
      self.bar.style.width = d ? (cur.video.currentTime / d) * 100 + '%' : '0%';
    };
    cur.video.addEventListener('timeupdate', this._onTime);

    this._syncMuteIcons();
    if (this.opts.onView) this.opts.onView(cur.item, i);
  };

  Player.prototype.togglePlay = function () {
    var r = this.reels[this.index];
    if (!r) return;
    if (r.video.paused) {
      safePlay(r.video);
      r.node.classList.remove('is-paused');
    } else {
      r.video.pause();
      r.node.classList.add('is-paused');
    }
  };

  Player.prototype.toggleMute = function () {
    this.muted = !this.muted;
    var r = this.reels[this.index];
    if (r) {
      r.video.muted = this.muted;
      if (!this.muted) safePlay(r.video);   // unmuting counts as a gesture
    }
    this._syncMuteIcons();
  };

  Player.prototype._syncMuteIcons = function () {
    var m = this.muted;
    this.reels.forEach(function (r) {
      r.mute.innerHTML = m ? ICON.soundOff : ICON.soundOn;
      r.mute.setAttribute('aria-label', m ? 'Unmute' : 'Mute');
    });
  };

  Player.prototype.go = function (i) {
    if (i < 0 || i >= this.reels.length) return;
    this.reels[i].node.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth' });
  };

  Player.prototype._keydown = function (e) {
    switch (e.key) {
      case 'Escape':    e.preventDefault(); this.close(); break;
      case 'ArrowDown': e.preventDefault(); this.go(this.index + 1); break;
      case 'ArrowUp':   e.preventDefault(); this.go(this.index - 1); break;
      case 'm': case 'M': this.toggleMute(); break;
      case ' ':         e.preventDefault(); this.togglePlay(); break;
      case 'Tab':       this._trap(e); break;
    }
  };

  /** Keep focus inside the dialog while it's open. */
  Player.prototype._trap = function (e) {
    var f = this.node.querySelectorAll('button:not([disabled])');
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  Player.prototype.open = function (i) {
    var self = this;
    this.lastFocus = document.activeElement;
    this.scrollY = global.scrollY;

    this.node.hidden = false;
    // Lock the page behind the dialog without losing scroll position.
    document.body.style.position = 'fixed';
    document.body.style.top = -this.scrollY + 'px';
    document.body.style.width = '100%';

    requestAnimationFrame(function () {
      self.node.classList.add('is-open');
      self.reels[i].node.scrollIntoView({ behavior: 'auto' });
      self._activate(i);
      self.closeBtn.focus({ preventScroll: true });
    });
  };

  Player.prototype.close = function () {
    var self = this;
    this.reels.forEach(function (r) { r.video.pause(); });
    this.node.classList.remove('is-open');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    global.scrollTo(0, this.scrollY);

    setTimeout(function () {
      self.node.hidden = true;
      if (self.lastFocus && self.lastFocus.focus) self.lastFocus.focus({ preventScroll: true });
    }, 200);
  };

  /* ==================================================================== Rail */

  function mount(node, options) {
    var opts = Object.assign(
      {
        items: [],
        currency: 'INR',
        locale: 'en-IN',
        onAddToCart: shopifyAddToCart,
        closeOnAdd: false,
        autoplayPreviews: true,
      },
      options || {}
    );

    var items = opts.items || [];
    if (!items.length) return null;

    node.classList.add('rw');
    node.innerHTML = '';

    var rail = el('div', 'rw__rail', { role: 'list' });
    var player = new Player(items, opts);
    var cards = [];

    items.forEach(function (item, i) {
      var p = item.product || {};
      var card = el('button', 'rw__card', {
        type: 'button',
        role: 'listitem',
        'aria-label': 'Play video' + (p.title ? ': ' + p.title : ''),
      });

      var media = el('div', 'rw__media');
      if (item.poster) media.appendChild(el('img', '', { src: item.poster, alt: '', loading: 'lazy' }));

      var video = el('video', '', { playsinline: '', loop: '', preload: 'none', muted: '' });
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.addEventListener('canplay', function () { video.classList.add('is-ready'); });
      media.appendChild(video);

      var v = views(item.views);
      if (v) {
        var vn = el('span', 'rw__views');
        vn.textContent = v;
        media.appendChild(vn);
      }

      // Card actions live inside a <button>, so they must not nest <button>s.
      var acts = el('div', 'rw__cardActions');
      var like = el('span', 'rw__icon', { role: 'button', tabindex: '-1', 'aria-hidden': 'true' });
      like.innerHTML = ICON.heart;
      like.addEventListener('click', function (e) {
        e.stopPropagation();
        like.classList.toggle('is-liked');
      });
      acts.appendChild(like);
      media.appendChild(acts);

      card.appendChild(media);

      if (p.title) {
        var prod = el('div', 'rw__product');
        prod.appendChild(el('img', 'rw__thumb', { src: p.image || item.poster, alt: '', loading: 'lazy' }));
        var info = el('div');
        var t = el('p', 'rw__title');
        t.textContent = p.title;
        info.appendChild(t);

        var price = el('div', 'rw__price');
        var now = el('span', 'rw__now');
        now.textContent = money(p.price, opts);
        price.appendChild(now);
        if (p.compareAt && p.compareAt > p.price) {
          var was = el('span', 'rw__was');
          was.textContent = money(p.compareAt, opts);
          price.appendChild(was);
        }
        info.appendChild(price);
        var off = discount(p);
        if (off) {
          var o = el('div', 'rw__off');
          o.textContent = off;
          info.appendChild(o);
        }
        prod.appendChild(info);
        card.appendChild(prod);
      }

      card.addEventListener('click', function () { player.open(i); });
      rail.appendChild(card);
      cards.push({ node: card, video: video, src: item.video });
    });

    node.appendChild(rail);

    // Desktop arrows
    var prev = el('button', 'rw__nav rw__nav--prev', { type: 'button', 'aria-label': 'Previous videos' });
    prev.innerHTML = ICON.prev;
    var next = el('button', 'rw__nav rw__nav--next', { type: 'button', 'aria-label': 'More videos' });
    next.innerHTML = ICON.next;
    function page(dir) {
      rail.scrollBy({ left: dir * (rail.clientWidth * 0.8), behavior: REDUCED ? 'auto' : 'smooth' });
    }
    prev.addEventListener('click', function () { page(-1); });
    next.addEventListener('click', function () { page(1); });
    node.appendChild(prev);
    node.appendChild(next);

    function syncNav() {
      prev.hidden = rail.scrollLeft < 8;
      next.hidden = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8;
    }
    rail.addEventListener('scroll', syncNav, { passive: true });
    global.addEventListener('resize', syncNav);
    syncNav();

    // Preview autoplay: load lazily, play only what's on screen.
    if (opts.autoplayPreviews && !REDUCED) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            var c = cards.find(function (x) { return x.node === e.target; });
            if (!c) return;
            if (e.isIntersecting) {
              if (!c.video.src) c.video.src = c.src;
              safePlay(c.video);
            } else {
              c.video.pause();
            }
          });
        },
        { root: rail, threshold: 0.5 }
      );
      cards.forEach(function (c) { io.observe(c.node); });

      // Don't burn battery/bandwidth on a backgrounded tab.
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) cards.forEach(function (c) { c.video.pause(); });
      });
    }

    return { node: node, player: player, open: function (i) { player.open(i || 0); } };
  }

  /* ------------------------------------------------------------ auto-init */

  async function autoInit() {
    var nodes = document.querySelectorAll('[data-reel-widget]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.dataset.rwMounted) continue;
      n.dataset.rwMounted = '1';
      var src = n.getAttribute('data-src');
      if (!src) continue;
      try {
        var res = await fetch(src);
        var json = await res.json();
        mount(n, {
          items: json.items || json,
          currency: n.getAttribute('data-currency') || 'INR',
          locale: n.getAttribute('data-locale') || 'en-IN',
        });
      } catch (e) {
        console.error('[ReelWidget] could not load', src, e);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }

  global.ReelWidget = { mount: mount, shopifyAddToCart: shopifyAddToCart };
})(window);
